import { isEmpty } from 'lodash-es';

import { ReportTypeID } from '~/src/dash/apiV2/reporting/reportTypes';

const hasOnlyAllowedDimensions = (
  selectedDimensionKeys: string[],
  allowedDimensionKeys: string[],
  defaultDisabled: boolean = false, // should metric be allowed on initial load
): boolean => {
  // return false if no selected dimensions
  if (isEmpty(selectedDimensionKeys)) return !defaultDisabled;
  for (const dimensionKey of selectedDimensionKeys) {
    if (!allowedDimensionKeys.includes(dimensionKey)) return false;
  }

  return true;
};

const hadAllowedDimensionKey = (
  selectedDimensionKeys: string[],
  allowedDimensionKeys: string[],
  defaultDisabled: boolean = false, // should metric be allowed on initial load
): boolean => {
  if (isEmpty(selectedDimensionKeys)) return defaultDisabled;
  for (const dimensionKey of selectedDimensionKeys) {
    if (allowedDimensionKeys.includes(dimensionKey)) return true;
  }

  return false;
};
interface ReportingUIMetricOverrides {
  checkIfDisabled: (selectedDimensionKeys: string[]) => boolean;
}

interface IReportingUIOverrides {
  metricOverrides: Record<string, ReportingUIMetricOverrides>;
}

type ReportingUIOverrides = Record<ReportTypeID, IReportingUIOverrides>;

const reportingUIOverrides: Partial<ReportingUIOverrides> = {
  [ReportTypeID.CampaignReport]: {
    metricOverrides: {
      impression_goal: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hasOnlyAllowedDimensions(selectedDimensionKeys, ['line_item_id', 'campaignname']),
      },
    },
  },
  [ReportTypeID.CampaignQuickReport]: {
    metricOverrides: {
      impression_goal: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hasOnlyAllowedDimensions(selectedDimensionKeys, ['line_item_id', 'campaignname'], true),
      },
    },
  },
  [ReportTypeID.SmartFloorsReport]: {
    // include all metrics here except for control_bid_request, response, won..
    // default state is the half_hour only metrics should be disabled
    // once half_hour is selected we disable the some metrics
    metricOverrides: {
      // half_hour only metrics:
      optimized_bid_requests: {
        checkIfDisabled: (
          selectedDimensionKeys, // checkIfDisabled -> true : disabled
        ) =>
          // if selectedDimensionKeys has half_hour (true) return false -> don't disable
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      low_bid_requests: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      high_bid_requests: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      optimized_bid_responses: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      low_bid_responses: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      high_bid_responses: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      optimized_bid_wons: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      low_bid_wons: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      high_bid_wons: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      optimized_avg_winning_bid: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      low_avg_winning_bid: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      high_avg_winning_bid: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      control_bid_floor: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      optimized_bid_floor: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      low_bid_floor: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      high_bid_floor: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      smart_bid_won_revenue_uplift: {
        checkIfDisabled: (selectedDimensionKeys) =>
          !hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      // non half_hour metrics:
      // remaining metrics are retrieved by joining with buyers_ext table

      // additionally these are default false (dont disable)
      smart_floors_uplift: {
        checkIfDisabled: (
          selectedDimensionKeys, // checkIfDisabled -> true : disabled
        ) =>
          // if selectedDimensionKeys not have half_hour -> return false -> don't disable
          // if selectedDimensionKeys empty -> and true param -> true
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      }, // calculate dby getting the below with buyers_ext table
      smart_bid_requests: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      }, // sum of low, high, optimized, don't need to get from buyers_ext
      smart_bid_responses: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      }, // sum of low, high, optimized
      smart_bid_wons: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      }, // sum of low, high, optimized
      smart_avg_winning_bid_price: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      }, // sum of low, high, optimized
      control_impressions: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      smart_impressions: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      control_revenue: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      smart_revenue: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      control_avg_ecpm: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
      smart_avg_ecpm: {
        checkIfDisabled: (selectedDimensionKeys) =>
          hadAllowedDimensionKey(selectedDimensionKeys, ['half_hour']),
      },
    },
  },
};

export const checkShouldUIDisableMetric = (
  selectedReportTypeID: ReportTypeID,
  metricKey: string,
  selectedDimensionKeys: string[],
): boolean => {
  const checkIfDisabled =
    reportingUIOverrides?.[selectedReportTypeID]?.metricOverrides?.[metricKey]?.checkIfDisabled;
  if (checkIfDisabled) {
    return checkIfDisabled(selectedDimensionKeys);
  }
  return false;
};
